import layout from './arenaAtlas.json';

/**
 * Where each picture lies in the arena's tile atlas (`public/textures/arena/`), from
 * `arenaAtlas.json`: the one list the atlas is built from (`tools/assets/arenaAtlas.ts`) and the
 * arena's meshes read their UVs from, so the two cannot drift apart. Pure, no Babylon: the build
 * script runs it in Node.
 *
 * A cell is a square of `cell` pixels: its picture, one metre of ground, and round it a gutter of
 * `gutter` pixels that repeats the picture's opposite edge, so a far-off tile's mip level blends
 * with its own continuation rather than with the next cell in the atlas.
 */
export interface AtlasMaterial {
  /** The ambientCG texture set the picture is made from (CC0). */
  source: string;
  /** Multiplies the colour, red, green and blue: a darker, greener swamp from the forest floor. */
  tint?: number[];
  /** 0 matte … 1 polished, scaled by the set's own roughness map. */
  gloss: number;
  /** The highlight's strength, 0…1. */
  specular: number;
}

/**
 * How a family's top shows its edges: `blob` against any neighbour of another family (grass
 * thins to dirt beside a road), `path` against any neighbour not on the path (a road's verges).
 */
export interface EdgeScheme {
  /** The material the edge fades to. */
  edge: string;
  /** How far in from the tile's side the edge reaches, as a share of the tile. */
  width: number;
  /** Walls, rocks and trees count as joined: grass grows up to a wall rather than stopping short. */
  joinsBlocks?: boolean;
}

export interface AtlasFamily {
  top: string;
  side: string;
  blob?: EdgeScheme;
  path?: EdgeScheme;
  /** A wall's top carries battlements along the sides it faces out on (the castle). */
  crenels?: boolean;
}

export interface AtlasLayout {
  cell: number;
  gutter: number;
  columns: number;
  materials: Record<string, AtlasMaterial>;
  families: Record<string, AtlasFamily>;
  fallback: string;
}

export const ATLAS: AtlasLayout = layout as AtlasLayout;

/** The four sides of a tile, as bits of the mask of its joined neighbours. */
export const NORTH = 1;
export const EAST = 2;
export const SOUTH = 4;
export const WEST = 8;
export const ALL_SIDES = NORTH | EAST | SOUTH | WEST;

/** One cell of the atlas: a material's plain picture, or a family's top with some edges. */
export type AtlasCell =
  | { kind: 'material'; material: string }
  | { kind: 'edges'; family: string; scheme: EdgeScheme; top: string; joined: number };

/**
 * Every cell in atlas order: the materials, then for each family with edges its top as it is
 * joined on each of the sixteen combinations of sides (all joined is the plain top again, and
 * still has its own cell, so a mask is always an index).
 */
export function atlasCells(atlas: AtlasLayout = ATLAS): AtlasCell[] {
  const cells: AtlasCell[] = Object.keys(atlas.materials).map((material) => ({
    kind: 'material',
    material,
  }));
  for (const [family, def] of Object.entries(atlas.families)) {
    const scheme = def.blob ?? def.path;
    if (!scheme) continue;
    for (let joined = 0; joined <= ALL_SIDES; joined++)
      cells.push({ kind: 'edges', family, scheme, top: def.top, joined });
  }
  return cells;
}

const CELLS = atlasCells();
const MATERIAL_CELL = new Map<string, number>();
const EDGE_CELL = new Map<string, number>();
CELLS.forEach((cell, i) => {
  if (cell.kind === 'material') MATERIAL_CELL.set(cell.material, i);
  else EDGE_CELL.set(`${cell.family}#${cell.joined}`, i);
});

export const ATLAS_ROWS = Math.ceil(CELLS.length / ATLAS.columns);
/** The atlas image's size in pixels. */
export const ATLAS_WIDTH = ATLAS.columns * ATLAS.cell;
export const ATLAS_HEIGHT = ATLAS_ROWS * ATLAS.cell;

/** The family's drawing, or the fallback's for a family the atlas does not know. */
export function familyOf(family: string): AtlasFamily {
  return (
    ATLAS.families[family] ??
    ATLAS.families[ATLAS.fallback] ?? { top: ATLAS.fallback, side: ATLAS.fallback }
  );
}

/** Whether a family's top shows edges, and by which rule. */
export function edgeScheme(family: string): 'blob' | 'path' | null {
  const def = ATLAS.families[family];
  return def?.blob ? 'blob' : def?.path ? 'path' : null;
}

/**
 * The cell a face shows: a side or bottom its family's side material; a top its family's top,
 * with the edges its unjoined sides call for when the family has an edge scheme.
 */
export function cellFor(
  family: string,
  face: 'top' | 'side' | 'bottom',
  joined = ALL_SIDES,
): number {
  const def = familyOf(family);
  if (face === 'top' && edgeScheme(family)) {
    const cell = EDGE_CELL.get(`${family}#${joined & ALL_SIDES}`);
    if (cell !== undefined) return cell;
  }
  const material = face === 'top' ? def.top : def.side;
  return MATERIAL_CELL.get(material) ?? MATERIAL_CELL.get(ATLAS.fallback) ?? 0;
}

export interface UvRect {
  u0: number;
  v0: number;
  u1: number;
  /** Top of the cell's picture (Babylon's v axis points up). */
  v1: number;
}

/** A cell's picture in UV space, inside its gutter. */
export function cellUv(cell: number): UvRect {
  const col = cell % ATLAS.columns;
  const row = Math.floor(cell / ATLAS.columns);
  const x0 = col * ATLAS.cell + ATLAS.gutter;
  const x1 = (col + 1) * ATLAS.cell - ATLAS.gutter;
  const y0 = row * ATLAS.cell + ATLAS.gutter;
  const y1 = (row + 1) * ATLAS.cell - ATLAS.gutter;
  return {
    u0: x0 / ATLAS_WIDTH,
    u1: x1 / ATLAS_WIDTH,
    v0: 1 - y1 / ATLAS_HEIGHT,
    v1: 1 - y0 / ATLAS_HEIGHT,
  };
}
